import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Grid, Stack } from '@mui/material';
import PageHeader from 'components/Header/Page';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { fetchSectorList, workspaceGetEnv } from 'service/service';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import LoaderComponent from 'components/Loader';
import FiltersBar from './FilterBar';
import { extractDatesFromNode, findSelectedNode } from 'utils/common-util';
import {
    useAdbFilters,
    useAdbHierarchyTable,
    commonFetchSectors,
    syncEnvWithOptions
} from 'utils/adb-common-util';
import { CellExpanderFormatter } from 'components/cell_expander_formatter';

const getColumns = (onExpand: (id: string) => void, onRowClick: (row: any) => void) => [
    {
        key: 'time_range',
        name: 'Time Range',
        formatter({ row, isCellSelected }: any) {
            const hasChildren = row.children && row.children.length > 0;
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', pl: row.level * 2 }}>
                    {hasChildren && (
                        <CellExpanderFormatter
                            isCellSelected={isCellSelected}
                            expanded={row.isExpanded}
                            onCellExpand={() => onExpand(row.id)}
                        />
                    )}
                    <span>{row.time_range}</span>
                </Box>
            );
        }
    },
    {
        key: 'count',
        name: 'Count',
        formatter({ row }: any) {
            return (
                <Box sx={{ cursor: 'pointer', color: '#4fc3f7', }} onClick={() => onRowClick(row)}>
                    {row.count ?? 0}
                </Box>
            );
        }
    }
];

export interface ClusterByCategoryServiceParams {
    startDate: string;
    endDate: string;
    sector: string;
    env: string;
    location: string;
    page: number;
    pageSize: number;
    selectedDateInString: string;
    clusterCategory: string;
}

interface ClusterByCategoryBaseProps {
    breadcrumbName: string;
    tableTitlePrefix: string;
    serviceFunc: (params: ClusterByCategoryServiceParams) => Promise<any>;
    navigationPath: string;
    preserveFilterKey: string;
    tableType: string;
}

const ClusterByCategoryBase: React.FC<ClusterByCategoryBaseProps> = ({
    breadcrumbName,
    tableTitlePrefix,
    serviceFunc,
    navigationPath,
    preserveFilterKey,
    tableType
}) => {
    const navigate = useNavigate();

    const {
        startDate, setStartDate, endDate, setEndDate,
        env, setEnv, sector, sectorName, setSectorName,
        location, setLocation, selectedDateInString, setSelectedDateInString,
        updateHistoryState, handleDateSelect, restoredRef, envRef, initialEnvAppliedRef, navigationState
    } = useAdbFilters(preserveFilterKey);

    const clusterCategory = useMemo(() => navigationState?.clusterCategory || '', [navigationState]);

    const [loader, setLoader] = useState(false);
    const [apiData, setApiData] = useState<any[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [envOptions, setEnvOptions] = useState<{ label: string; value: string }[]>([]);
    const [sectorList, setSectorList] = useState<string[]>([]);

    const { tableData, toggleExpandRow, paginationData, rowsRef } = useAdbHierarchyTable(apiData, pageSize);

    useEffect(() => {
        commonFetchSectors(fetchSectorList, setSectorList);
    }, []);

    useEffect(() => {
        const getEnvFetch = async (loc: string) => {
            try {
                const res = await workspaceGetEnv(loc);
                const options = [{ label: 'ALL', value: 'both' }, ...(res?.data || [])];
                setEnvOptions(options);
                syncEnvWithOptions(options, initialEnvAppliedRef, envRef, navigationState?.env, setEnv);
            } catch (err) {
                console.error('workspaceGetEnv error', err);
            }
        };
        getEnvFetch(location);
    }, [location, navigationState?.env]);

    useEffect(() => {
        const fetch = async () => {
            setLoader(true);
            try {
                const resp = await serviceFunc({
                    startDate,
                    endDate,
                    sector,
                    env,
                    location,
                    page,
                    pageSize,
                    selectedDateInString,
                    clusterCategory: clusterCategory.toUpperCase()
                });
                if (resp?.success) {
                    const data = resp.data || [];
                    setApiData(data);
                    const total = data.reduce((acc: number, item: any) => {
                        const top = item.week || item.month || item.year || item.day;
                        const key = Object.keys(item)[0];
                        const val = top?.total_count ?? top?.total_cluster_cost ?? item[key]?.total_count ?? item[key]?.total_cluster_cost ?? 0;
                        return acc + Number(val);
                    }, 0);
                    setTotalCount(total);
                } else {
                    setApiData([]);
                    setTotalCount(0);
                }
            } catch (err) {
                console.error(err);
                setApiData([]);
                setTotalCount(0);
            } finally {
                setLoader(false);
                if (restoredRef.current) {
                    sessionStorage.removeItem(preserveFilterKey);
                    restoredRef.current = false;
                }
            }
        };
        fetch();
    }, [startDate, endDate, env, sector, location, selectedDateInString, clusterCategory, pageSize, page, serviceFunc, preserveFilterKey]);

    const handleRowClick = useCallback((row: any) => {
        let type: 'year' | 'month' | 'week' | 'day' = 'year';
        if (row.id.includes('day')) type = 'day';
        else if (row.id.includes('week')) type = 'week';
        else if (row.id.includes('month')) type = 'month';

        const found = findSelectedNode(apiData, type, row.time_range, row.id);
        const payload = found ? found : { kind: 'full', node: apiData };

        const navStateBase: any = {
            dateLabel: row.time_range,
            apiData: payload,
            type,
            startDate, endDate, env, sector, location, selectedDateInString,
            computeType: navigationState?.computeType || clusterCategory || undefined,
            clusterCategory
        };

        if (found?.kind) {
            const dates = extractDatesFromNode(found.node);
            if (found.kind === 'day' && found.node?.date) {
                const d = moment(found.node.date, 'YYYY-MM-DD').format('YYYY-MM-DD');
                navStateBase.startDate = d;
                navStateBase.endDate = d;
                navStateBase.selectedDateInString = 'customDateRange';
            } else if (dates.length > 0) {
                const moments = dates.map((ds: string) => moment(ds, 'YYYY-MM-DD'));
                navStateBase.startDate = moment.min(moments).format('YYYY-MM-DD');
                navStateBase.endDate = moment.max(moments).format('YYYY-MM-DD');
                navStateBase.selectedDateInString = 'customDateRange';
            }
            setPage(1);
            rowsRef.current = [];
        }

        sessionStorage.setItem(preserveFilterKey, JSON.stringify({ startDate, endDate, env, sector, location, selectedDateInString }));
        navigate(navigationPath, { state: navStateBase });
    }, [navigate, apiData, startDate, endDate, env, sector, location, selectedDateInString, clusterCategory, navigationState, rowsRef, navigationPath, preserveFilterKey]);

    const columns = useMemo(() => getColumns(toggleExpandRow, handleRowClick), [toggleExpandRow, handleRowClick]);

    const handlePageChange = (_event: any, value: number) => setPage(value);
    const handlePageSizeChange = (event: any) => { setPageSize(Number(event.target.value)); setPage(1); };

    const goToClusterAnalytics = () => {
        navigate('/databricksADB/clusteranalytics', {
            state: { startDate, endDate, env, sector, location, selectedDateInString }
        });
    };

    const renderTableContent = () => {
        if (loader) {
            return <LoaderComponent pt={20} />;
        }
        if (tableData.length === 0) {
            return (
                <Box sx={{ textAlign: 'center', py: 10, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                    <Box sx={{ fontSize: '1.2rem', color: '#666', mb: 1 }}>No Data Available</Box>
                    <Box sx={{ fontSize: '0.9rem', color: '#999' }}>No data found for the selected date range and filters.</Box>
                </Box>
            );
        }
        return (
            <CustomizedDataGrid
                title={`${tableTitlePrefix} (${totalCount.toLocaleString()})`}
                tableColumns={columns}
                tableData={tableData}
                tableType={tableType}
                defaultColumnNames={['Time Range', 'Count']}
                page={page} pageSize={pageSize}
                paginationData={paginationData}
                listLength={tableData.length}
                handlepageSizeChange={handlePageSizeChange}
                handlePageChange={handlePageChange}
                isDateRangeFilter={false} isDisabled={false} isFetching={loader}
            />
        );
    };

    return (
        <Box bgcolor="backgroundLight.primary" sx={{ mt: { xs: '70px', sm: '60px', md: '80px' } }}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2, px: 2 }}>
                <Box sx={{ mb: 2, fontSize: '1.125rem', fontWeight: '600', color: '#003771' }}>
                    <span style={{ cursor: 'pointer', color: '#003771' }} onClick={goToClusterAnalytics}>
                        Cluster Analytics
                    </span>
                    {' > '}
                    <span>
                        {breadcrumbName}
                        {clusterCategory ? ` - ${clusterCategory.toString().toUpperCase()}` : ''}
                    </span>
                </Box>
                <Box sx={{ position: 'sticky', top: 64, zIndex: 10 }}>
                    <PageHeader title=" " icon={''} lastSync={null}>
                        <FiltersBar
                            location={location} setLocation={setLocation}
                            env={env} setEnv={setEnv}
                            envOptions={envOptions} setEnvOptions={setEnvOptions}
                            startDate={startDate} endDate={endDate}
                            setStartDate={setStartDate} setEndDate={setEndDate}
                            handleDateSelect={handleDateSelect}
                            selectedDateInString={selectedDateInString}
                            setSelectedDateInString={setSelectedDateInString}
                            sectorName={sectorName} setSectorName={setSectorName}
                            updateHistoryState={updateHistoryState}
                            sectorOptionsList={sectorList}
                        />
                    </PageHeader>
                </Box>
            </Stack>

            <Grid container spacing={2} px={2}>
                <Grid item xs={12}>
                    {renderTableContent()}
                </Grid>
            </Grid>
        </Box>
    );
};

export default ClusterByCategoryBase;
